import {
  useEffectWithController,
  useLoadableState
} from '@carvana/merch-react-hooks';
import { isSuccess, Result } from '@carvana/merch-result';
import { useContext } from 'react';
import { type BluesteelResponse } from '~/app/api/v1/bluesteel/route';
import { axios } from '~/clients';
import { getBlueSteel } from '~/constants/endpoints';
import StaticContext from '~/context/common/StaticContext';
import TradeContext from '~/context/common/TradeContext';
import { generateErrorLogger } from '~/utilities/loggers';

type UrlParams = {
  year: number;
  make: string;
  model: string;
  trim: string;
  color: string;
};

async function fetchImageUrl(
  { year, make, model, trim, color }: UrlParams,
  correlationId: string,
  controller: AbortController
): Promise<string> {
  const url = getBlueSteel({ currentPageUrl: window.location.href });
  const query = new URLSearchParams({
    year: String(year),
    make,
    model,
    trim,
    color
  }).toString();

  const { data } = await axios.get<Result<BluesteelResponse>>(
    `${url}?${query}`,
    {
      headers: {
        'x-client-id': 'e902dfb1-f373-450d-8cca-9db2e12bfce5',
        'x-correlation-id': correlationId
      },
      signal: controller.signal
    }
  );

  if (isSuccess(data)) {
    return data.content.bluesteelImageUrl;
  }

  return '';
}

export const isNonGenericImage = (url: string): boolean => {
  return Boolean(url) && !url.includes('generic');
};

export default function useTradeVehicleImage(componentName: string) {
  const { eventCorrelationId } = useContext(StaticContext);
  const { selectedTrade } = useContext(TradeContext);
  const {
    data: imageUrl,
    isLoaded,
    setData,
    setError,
    setLoading
  } = useLoadableState<string>('');
  const { vehicle } = selectedTrade ?? {};
  const showImage =
    isLoaded && Boolean(imageUrl) && isNonGenericImage(imageUrl);

  useEffectWithController(
    controller => {
      if (!vehicle) return;
      setLoading();

      fetchImageUrl(vehicle, eventCorrelationId, controller)
        .then(setData)
        .catch(generateErrorLogger(componentName, {}, { callback: setError }));
    },
    [eventCorrelationId, setData, setError, setLoading, vehicle, componentName]
  );

  return { imageUrl, isLoaded, showImage };
}
